import * as React from "react";
import type { Session } from "@supabase/supabase-js";
import type { CreateWorkspaceResponse, SessionResponse } from "@taskiq/shared";
import { oauthProvider } from "@taskiq/shared";
import { clearActiveScope, initActiveScope } from "./active-scope";
import { api, ApiError } from "./api";
import { supabase, supabaseNoPkce } from "./supabase";
import { AuthContext, type AuthApi, type AuthState } from "./auth-context";

/**
 * The provider, and nothing else at runtime.
 *
 * The context, the hook and the state types live in `auth-context.ts` so this
 * module exports exactly one component - which is what lets React Fast Refresh
 * treat it as a boundary instead of re-evaluating it and minting a fresh
 * context that mounted consumers do not hold (console-ui.md, rule 7).
 */

const INITIAL: AuthState = {
  status: "loading",
  session: null,
  user: null,
  scope: null,
  capabilities: [],
  pendingReason: null,
  error: null,
};

/**
 * Which identity providers are attached to a session.
 *
 * GoTrue has written this two ways over the years - a `providers` array and a
 * singular `provider` string - and a project can hold sessions issued by both
 * across a refresh-token lifetime. Reading only one of them makes the password
 * step appear or vanish depending on how old the session is.
 */
function providersOf(session: Session | null): string[] {
  const meta = session?.user.app_metadata;
  if (!meta) return [];
  const many = Array.isArray(meta.providers) ? (meta.providers as string[]) : [];
  const one = typeof meta.provider === "string" ? [meta.provider] : [];
  return [...new Set([...many, ...one])];
}

/**
 * The record of an emailed token this tab has already spent.
 *
 * See `redeemAndSetPassword` for why it exists. It stores the id the redemption
 * produced alongside the hash, and `alreadyRedeemed` only answers yes when the
 * live session is still that same user - so this can shorten a retry and can
 * never redirect one onto another account.
 */
const REDEEMED_KEY = "taskiq.auth.redeemed";

function rememberRedemption(tokenHash: string, userId: string): void {
  try {
    sessionStorage.setItem(REDEEMED_KEY, JSON.stringify({ tokenHash, userId }));
  } catch {
    // Private-mode Safari and a full quota both throw here. Losing the record
    // costs a retry the clearer error, never correctness - the next submit just
    // takes the ordinary verify-then-update path.
  }
}

function forgetRedemption(tokenHash: string): void {
  try {
    const raw = sessionStorage.getItem(REDEEMED_KEY);
    if (raw && (JSON.parse(raw) as { tokenHash?: string }).tokenHash === tokenHash) {
      sessionStorage.removeItem(REDEEMED_KEY);
    }
  } catch {
    // As above.
  }
}

async function alreadyRedeemed(tokenHash: string): Promise<boolean> {
  let recorded: { tokenHash?: string; userId?: string } | null = null;
  try {
    const raw = sessionStorage.getItem(REDEEMED_KEY);
    recorded = raw ? (JSON.parse(raw) as { tokenHash?: string; userId?: string }) : null;
  } catch {
    return false;
  }

  if (!recorded || recorded.tokenHash !== tokenHash) return false;

  const { data } = await supabase.auth.getSession();
  return Boolean(data.session) && data.session?.user.id === recorded.userId;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<AuthState>(INITIAL);

  /**
   * Resolve the profile behind a Supabase session.
   *
   * The 403 branches are the interesting ones, and they are three different
   * things wearing the same status code. `onboarding_required` is a brand-new
   * self sign-up who has not created a workspace - the most ordinary state in
   * the product. `account_disabled` is a revoked account, which an administrator
   * at their own company can reverse. `company_disabled` is a suspended tenant,
   * which nobody inside it can. None is a failure: treating them as one would
   * sign the user out (so they never learn why) or paint a red banner over
   * something entirely expected.
   */
  const loadProfile = React.useCallback(async (session: Session) => {
    try {
      /**
       * The second factor comes before the profile, and it has to.
       *
       * `signInWithPassword` returns a perfectly real session for an account
       * with TOTP enabled - just one at `aal1`. Nothing about that session looks
       * incomplete from the client's side, so an app that does not ask is an app
       * where enabling 2FA changes nothing at all. `nextLevel` is GoTrue saying
       * "this user has a verified factor"; `currentLevel` is whether it has been
       * used on this session.
       *
       * The API refuses `aal1` for an account with a factor as well - see
       * `requireAuth` - so this is the pleasant version of a refusal that
       * happens either way, not the enforcement itself.
       */
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal?.currentLevel === "aal1" && aal.nextLevel === "aal2") {
        setState({ ...INITIAL, status: "mfa", session });
        return;
      }

      const me = await api<SessionResponse>("/auth/me");

      // Seeded before the console mounts, so the first site-scoped path a page
      // builds is already the right one. Idempotent per user id, so a token
      // refresh does not undo a site the user switched to.
      initActiveScope(me.user.id, me.user.company.id, me.user.site.id);

      setState({
        status: "ready",
        session,
        user: me.user,
        scope: me.scope,
        capabilities: me.capabilities,
        pendingReason: null,
        error: null,
      });
    } catch (err) {
      if (err instanceof ApiError && err.needsOnboarding) {
        setState({ ...INITIAL, status: "onboarding", session });
        return;
      }

      if (err instanceof ApiError && err.isPending) {
        setState({
          ...INITIAL,
          status: "pending",
          session,
          pendingReason: err.message,
        });
        return;
      }

      // 401 already cleared the session in api.ts; the auth listener below will
      // fire and move us to signed-out. Anything else - a network failure, a
      // 500, an unexpected code - is an outage, not a verdict on the account,
      // so it gets its own state rather than borrowing `pending`'s copy.
      setState({
        ...INITIAL,
        status: session ? "error" : "signed-out",
        session,
        error: err instanceof ApiError ? err.message : "Could not load your account",
      });
    }
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    /**
     * One listener drives every transition - initial load, sign-in, sign-out,
     * token refresh and the password-recovery deep link all arrive here. Doing
     * it in one place is what stops the "signed in but the UI still says signed
     * out" class of bug.
     */
    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;

      if (!session) {
        // The scope belongs to a signed-in user. Leaving it set would point the
        // next sign-in at the previous account's site until their own profile
        // landed to correct it.
        clearActiveScope();
        setState({ ...INITIAL, status: "signed-out" });
        return;
      }

      // A refresh reissues the token but changes nothing about who the user is.
      // Re-fetching the profile on every hourly refresh would be pure noise.
      if (event === "TOKEN_REFRESHED") {
        setState((prev) => ({ ...prev, session }));
        return;
      }

      void loadProfile(session);
    });

    // onAuthStateChange fires INITIAL_SESSION on subscribe, so there is no
    // separate bootstrap path to keep in step with the one above.

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const value = React.useMemo<AuthApi>(
    () => ({
      ...state,

      can: (capability) => state.capabilities.includes(capability),

      /**
       * Read from the verified identity, not from anything the client remembers.
       *
       * `app_metadata` is service-role-writable only, so `providers` is one of
       * the few things on the session a client may trust. A user who arrived
       * through Google has no password and must not be asked to invent one; a
       * user who arrived through the email code has an identity with no
       * credential and must be. Tracking this in component state instead would
       * lose it on the refresh that happens between step two and step three.
       */
      needsPassword: providersOf(state.session).includes("email"),

      async signIn(email, password, captchaToken) {
        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim().toLowerCase(),
          password,
          options: { captchaToken },
        });
        if (error) throw error;
        // No setState here - the auth listener owns every transition.
      },

      /**
       * Self sign-up, step one: create the identity and email a code.
       *
       * `signInWithOtp` rather than `signUp`, and the difference is the whole
       * point: `signUp` demands a password up front and then proves the address
       * with an emailed link, while this proves the address first and lets the
       * password wait until step three. See `startSignUpSchema` for why the
       * emailed link had to go - mail scanners consume single-use GET links, and
       * the PKCE verifier cannot survive reading the mail on a different device.
       *
       * `shouldCreateUser: true` means this doubles as a sign-in for an address
       * that already exists, which is deliberate. The alternative is to probe
       * first and tell an anonymous caller whether an address is registered,
       * which is an account-enumeration oracle handed out for free. The verify
       * step routes an existing user to their console instead.
       *
       * `full_name` and `phone` go into `user_metadata`, which is user-writable
       * and therefore trusted for **display only** - never as tenancy. Both are
       * facts a person is stating about themselves and can rewrite from their
       * own profile page anyway, so there is nothing here to escalate with; the
       * rule that matters is the one in the root CLAUDE.md, that no company, site
       * or role may ever be read from this channel.
       *
       * They are carried in metadata rather than as a parameter because of where
       * they are needed. There is no session and no profile at this point - the
       * `public.users` row is not written until step four, by
       * `public.create_workspace`, which is three screens and possibly a device
       * away. GoTrue is the only thing that remembers anything in between, and it
       * already carried `full_name` across that gap for exactly this reason.
       */
      async startSignUp(input, captchaToken) {
        const { error } = await supabase.auth.signInWithOtp({
          email: input.email.trim().toLowerCase(),
          options: {
            shouldCreateUser: true,
            captchaToken,
            data: { full_name: input.full_name, phone: input.phone ?? null },
          },
        });
        if (error) throw error;
      },

      async resendEmailCode(email, captchaToken) {
        const { error } = await supabase.auth.signInWithOtp({
          email: email.trim().toLowerCase(),
          options: { shouldCreateUser: true, captchaToken },
        });
        if (error) throw error;
      },

      /**
       * Self sign-up, step two: the code becomes a session.
       *
       * `type: "email"` covers both cases GoTrue can be in here - a brand-new
       * identity confirming its address, and an existing one signing in - so
       * there is no branch to get wrong. The auth listener above picks the
       * session up and decides where the user goes from their profile, which is
       * what routes an existing account to its console rather than through
       * onboarding a second time.
       */
      async verifyEmailCode(email, code) {
        const { error } = await supabase.auth.verifyOtp({
          email: email.trim().toLowerCase(),
          token: code.replace(/\D/g, ""),
          type: "email",
        });
        if (error) throw error;
      },

      /**
       * Social sign-in. Redirects away and never returns - the browser comes
       * back to `/auth/callback`, where the SDK exchanges the code and the auth
       * listener above picks it up like any other sign-in.
       *
       * The same call serves sign-in and sign-up, because from Supabase's side
       * they are the same operation: if the identity is new it is created, and
       * the trigger then either finds an invitation or leaves the user in the
       * onboarding state. Rendering these buttons on both screens is therefore
       * correct rather than duplication.
       */
      async signInWithProvider(provider) {
        const { error } = await supabase.auth.signInWithOAuth({
          provider,
          options: {
            redirectTo: `${window.location.origin}/auth/callback`,
            scopes: oauthProvider(provider).scopes,
          },
        });
        if (error) throw error;
      },

      /**
       * Self sign-up, step two. The response carries a full session, so the
       * console can go straight to the app rather than round-tripping
       * `/auth/me` while the user watches a spinner on their very first screen.
       */
      async createWorkspace(input) {
        const result = await api<CreateWorkspaceResponse>("/auth/workspace", {
          method: "POST",
          body: input,
        });

        setState((prev) => ({
          ...prev,
          status: "ready",
          user: result.session.user,
          scope: result.session.scope,
          capabilities: result.session.capabilities,
          pendingReason: null,
          error: null,
        }));

        return result;
      },

      async signOut() {
        await supabase.auth.signOut();
      },

      /**
       * On `supabaseNoPkce`, **not** on the main client. The whole reason that
       * second client exists is this one call: under PKCE, GoTrue emits a
       * `pkce_`-prefixed token hash that `verifyOtp` can never redeem, so every
       * reset link fails as "expired" the moment it is opened. `lib/supabase.ts`
       * has the full mechanism.
       *
       * `redirectTo` is belt-and-braces. `recovery.html` hardcodes
       * `{{ .SiteURL }}/reset-password` and ignores `{{ .RedirectTo }}`, but
       * passing it keeps the request honest against the project's
       * `additional_redirect_urls` allow-list, which already lists that path.
       *
       * GoTrue answers the same way for an address that does not exist, so this
       * cannot be used to enumerate accounts.
       */
      async sendPasswordReset(email, captchaToken) {
        const { error } = await supabaseNoPkce.auth.resetPasswordForEmail(
          email.trim().toLowerCase(),
          { redirectTo: `${window.location.origin}/reset-password`, captchaToken }
        );
        if (error) throw error;
      },

      /**
       * Redeem an emailed token and set a password, in that order, in one
       * action.
       *
       * **The ordering is the security property.** The obvious design redeems
       * the token when the link is opened and then shows a password form, but
       * that makes a GET consume the invitation: any mail scanner that follows
       * links - and increasingly, any one that renders them - burns the token
       * before the human arrives. So `/accept-invite` and `/reset-password`
       * render their form against an unverified token and call this on submit.
       * A scanner fetching the URL gets HTML and changes nothing.
       *
       * `verifyOtp` produces the session that `updateUser` then needs, so the
       * two cannot be reordered or split.
       *
       * ── Redeeming exactly once, however many times the form is submitted ──
       *
       * A token is single-use and GoTrue has no way to hand one back, so the
       * moment `verifyOtp` returns, that link is spent - **even if the password
       * is then rejected.** And it will be rejected sometimes: the server's
       * password policy is not knowable from here. `minimum_password_length`
       * lives in `config.toml`, a hosted project can add character-class
       * requirements and leaked-password checking in its Auth settings, and none
       * of it is exposed to a client. `setPasswordSchema` enforces what we do
       * know (ten characters) and GoTrue owns the rest, which is why
       * `weak_password` can come back on a password this form was happy with.
       *
       * That produced a bug that read as nonsense: reject the password, user
       * types a better one, presses the button again, and the *second* attempt
       * fails with `otp_expired` on a link they opened a minute ago. The token
       * was gone after the first press and the second `verifyOtp` had nothing
       * left to redeem.
       *
       * So a redemption is recorded and never repeated. The second submit skips
       * `verifyOtp` entirely and goes straight to `updateUser`, using the
       * session the first one already produced - the link stays usable until a
       * password has actually been set, which is what it should have meant all
       * along.
       *
       * **The record is `(token hash → the user id it produced)`, and the id
       * half is not optional.** Keyed on the hash alone, someone signed in as
       * one person who opens another person's invitation link would skip the
       * verify and set *their own* password on a submit meant for a different
       * account. Requiring the live session to be the one that token minted
       * makes that impossible. `sessionStorage` rather than a ref, so a reload
       * of the tab mid-flow is still recoverable, and per-tab rather than
       * per-origin so it cannot outlive the sitting it belongs to.
       */
      async redeemAndSetPassword(tokenHash, type, password) {
        if (!(await alreadyRedeemed(tokenHash))) {
          const { data, error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type,
          });
          if (verifyError) throw verifyError;

          const userId = data.user?.id;
          if (userId) rememberRedemption(tokenHash, userId);
        }

        const { error } = await supabase.auth.updateUser({
          password,
          data: { password_set: true },
        });
        if (error) throw error;

        // Only now is the link genuinely finished with. Forgetting it here stops
        // a spent record sitting in the tab for a flow that has completed.
        forgetRedemption(tokenHash);
      },

      /**
       * The `password_set` breadcrumb is a **UX flag, not a security control**.
       *
       * Onboarding asks for a password before it asks for a company, so there is
       * a window where a refresh would put the user back on the password screen
       * with a password they have already chosen. Typing it again returns
       * `same_password` and the flow dead-ends.
       *
       * `user_metadata` is writable by the user it belongs to, so nothing may
       * ever read this as authorisation. It is safe here precisely because the
       * worst a forged value can do is skip a screen the user is free to skip by
       * doing nothing. Whether a password actually exists is GoTrue's business,
       * and sign-in is where that gets tested.
       */
      /**
       * The code, then a fresh profile load.
       *
       * `verify` issues a new session at `aal2`, which fires the auth listener
       * and re-runs `loadProfile` - so there is deliberately no `setState` here.
       * The one place every transition is decided stays the one place.
       */
      async verifyMfa(code) {
        const { data: factors, error: listError } = await supabase.auth.mfa.listFactors();
        if (listError) throw listError;

        const factor = factors?.totp?.[0];
        if (!factor) throw new Error("No authenticator app is set up for this account.");

        const { error } = await supabase.auth.mfa.challengeAndVerify({
          factorId: factor.id,
          code: code.replace(/\D/g, ""),
        });
        if (error) throw error;
      },

      async updatePassword(password, currentPassword) {
        const { error } = await supabase.auth.updateUser({
          password,
          current_password: currentPassword,
          data: { password_set: true },
        });
        if (error) throw error;
      },

      async refresh() {
        const { data } = await supabase.auth.getSession();
        if (data.session) await loadProfile(data.session);
      },
    }),
    [state, loadProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
